import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Globe, Palette, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import PublishSiteModal from "../components/PublishSiteModal";

const STEPS = ["Business", "Style", "Audience", "Generate"];

export default function AIBuilder() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const claimJobId = params.get("job");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    business_name: "", industry: "", color_preference: "cyan",
    website_type: "business", services: "", target_audience: "", tone: "professional",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [showPublish, setShowPublish] = useState(false);

  // If we arrived from a signup redirect with ?job=<id>, claim that anon job
  useEffect(() => {
    if (!user || !claimJobId) return;
    api.post("/ai/public/claim", { job_id: claimJobId })
      .then(() => api.get(`/ai/public/preview/${claimJobId}`))
      .then(r => {
        setJobId(r.data.id);
        setResult(r.data.output);
        setStep(3);
        toast.success("Preview claimed — subscribe to publish.");
      })
      .catch(() => {});
  }, [user, claimJobId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const generate = async () => {
    if (!user) { toast.error("Please login first"); nav("/login?next=/ai-builder"); return; }
    setLoading(true);
    try {
      const payload = { ...form, services: form.services.split(",").map(s=>s.trim()).filter(Boolean) };
      const { data } = await api.post("/ai/website-builder", payload);
      setResult(data.output);
      setJobId(data.id);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  // Deprecated inline deploy — use modal now
  const openPublish = () => { if (jobId) setShowPublish(true); };

  return (
    <div className="section max-w-4xl">
      <div className="text-center mb-8">
        <div className="tag mb-3">AI Website Builder</div>
        <h1 className="text-5xl font-black tracking-tighter" style={{fontFamily:'Outfit'}}>
          From <span className="text-gradient-cyan">brief</span> to <span className="text-gradient-cyan">blueprint</span>.
        </h1>
        <p className="text-gray-400 mt-3">Tell us about your business — get a full website blueprint in 30 seconds.</p>
      </div>

      <div className="flex justify-between mb-6" data-testid="builder-steps">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1 flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i<=step ? "bg-cyan-400 text-black" : "bg-white/5 text-gray-400 border border-white/10"}`}>{i+1}</div>
            <div className={`hidden sm:block ml-2 text-xs uppercase tracking-wider ${i<=step ? "text-cyan-300" : "text-gray-500"}`}>{s}</div>
            {i < STEPS.length-1 && <div className="flex-1 h-[1px] bg-white/10 mx-3" />}
          </div>
        ))}
      </div>

      <div className="card p-6 sm:p-8">
        {step === 0 && (
          <div className="space-y-4">
            <div>
              <label className="label">Business Name</label>
              <input data-testid="builder-business-name" value={form.business_name} onChange={set("business_name")} className="input w-full" placeholder="Acme Studios" />
            </div>
            <div>
              <label className="label">Industry</label>
              <input data-testid="builder-industry" value={form.industry} onChange={set("industry")} className="input w-full" placeholder="SaaS, Restaurant, Real Estate..." />
            </div>
            <div className="flex justify-end"><button onClick={()=>setStep(1)} disabled={!form.business_name || !form.industry} className="btn-primary inline-flex items-center gap-2" data-testid="builder-next-1">Next <ArrowRight size={14}/></button></div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="label">Website Type</label>
              <select data-testid="builder-type" value={form.website_type} onChange={set("website_type")} className="input w-full">
                <option value="business">Business / Corporate</option>
                <option value="portfolio">Portfolio</option>
                <option value="ecommerce">Ecommerce</option>
                <option value="saas">SaaS Marketing</option>
                <option value="blog">Blog / Magazine</option>
              </select>
            </div>
            <div>
              <label className="label">Color Preference</label>
              <select data-testid="builder-color" value={form.color_preference} onChange={set("color_preference")} className="input w-full">
                <option value="cyan">Electric Cyan</option>
                <option value="blue">Deep Blue</option>
                <option value="green">Forest Green</option>
                <option value="orange">Warm Orange</option>
                <option value="violet">Royal Violet</option>
                <option value="monochrome">Monochrome</option>
              </select>
            </div>
            <div>
              <label className="label">Tone</label>
              <select data-testid="builder-tone" value={form.tone} onChange={set("tone")} className="input w-full">
                <option>professional</option><option>playful</option><option>luxury</option><option>technical</option><option>friendly</option>
              </select>
            </div>
            <div className="flex justify-between"><button onClick={()=>setStep(0)} className="btn-ghost">Back</button><button onClick={()=>setStep(2)} className="btn-primary inline-flex items-center gap-2">Next <ArrowRight size={14}/></button></div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="label">Services (comma-separated)</label>
              <input data-testid="builder-services" value={form.services} onChange={set("services")} className="input w-full" placeholder="Consulting, Implementation, Training" />
            </div>
            <div>
              <label className="label">Target Audience</label>
              <input data-testid="builder-audience" value={form.target_audience} onChange={set("target_audience")} className="input w-full" placeholder="Startup founders, Marketing managers..." />
            </div>
            <div className="flex justify-between">
              <button onClick={()=>setStep(1)} className="btn-ghost">Back</button>
              <button onClick={generate} disabled={loading} className="btn-primary inline-flex items-center gap-2" data-testid="builder-generate">
                {loading ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16} />} Generate Website
              </button>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div data-testid="builder-result" className="space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>{form.business_name} — Blueprint</h2>
              <button onClick={()=>{setStep(0); setResult(null);}} className="btn-ghost !py-2 !px-4">Generate Another</button>
            </div>
            {result.tagline && <div><div className="text-cyan-400 text-xs uppercase tracking-wider mb-1">Tagline</div><div className="text-white text-lg">{result.tagline}</div></div>}
            {result.hero_headline && <div><div className="text-cyan-400 text-xs uppercase tracking-wider mb-1">Hero</div><div className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>{result.hero_headline}</div><div className="text-gray-300 mt-1">{result.hero_subheadline}</div></div>}
            {result.color_palette && (
              <div>
                <div className="text-cyan-400 text-xs uppercase tracking-wider mb-2">Color Palette</div>
                <div className="flex gap-2">{result.color_palette.map((c,i)=>(<div key={i} style={{background:c}} className="w-12 h-12 rounded-md border border-white/10" title={c}/>))}</div>
              </div>
            )}
            {result.pages && (
              <div>
                <div className="text-cyan-400 text-xs uppercase tracking-wider mb-2">Pages</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {result.pages.map((p,i)=>(
                    <div key={i} className="border border-white/10 rounded-lg p-3 bg-white/[0.02]">
                      <div className="font-semibold text-white">{p.name}</div>
                      <div className="text-xs text-gray-500">/{p.slug}</div>
                      {p.sections && <ul className="mt-2 text-sm text-gray-300 space-y-1">{p.sections.slice(0,4).map((s,j)=><li key={j} className="truncate">• {s.heading || s.type}</li>)}</ul>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {result.seo && (
              <div>
                <div className="text-cyan-400 text-xs uppercase tracking-wider mb-2">SEO</div>
                <div className="text-white font-semibold">{result.seo.title}</div>
                <div className="text-gray-400 text-sm">{result.seo.description}</div>
                {result.seo.keywords && <div className="mt-2 flex flex-wrap gap-2">{result.seo.keywords.map((k,i)=><span key={i} className="tag">{k}</span>)}</div>}
              </div>
            )}
            <div className="pt-4 border-t border-white/10 flex flex-wrap gap-3">
              <button
                onClick={openPublish}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="deploy-site-btn"
              >
                <Globe size={16}/> Publish site live
              </button>
              <button className="btn-ghost" onClick={()=>nav("/pricing?cat=ai_builder")}>View Builder Plans</button>
              <button className="btn-ghost" onClick={()=>nav("/dashboard")}>View in Dashboard</button>
            </div>
            {showPublish && (
              <PublishSiteModal
                aiJobId={jobId}
                defaultSlug={(form.business_name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 32)}
                onClose={() => setShowPublish(false)}
                onPublished={() => {}}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
